import {
  faCalendarCheck,
  faCalendarEdit,
  faCalendarPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import Avatar from 'src/components/ui/Avatar/Avatar';
import Button from 'src/components/ui/Button/Button';
import CustomerDetails from 'src/components/ui/CustomerDetails/CustomerDetails';
import DateTooltip from 'src/components/ui/DateTooltip/DateTooltip';
import DetectedErrors from 'src/components/ui/DetectedErrors/DetectedErrors';
import HeatingApplianceDetails from 'src/components/ui/HeatingApplianceDetails/HeatingApplianceDetails';
import PageHeader from 'src/components/ui/PageHeader/PageHeader';
import StatusIndicator from 'src/components/ui/StatusIndicator/StatusIndicator';
import TicketNotes from 'src/components/ui/TicketNotes/TicketNotes';
import { space } from 'src/constants/transConstants';
import { routes } from 'src/routes';
import {
  getDeviceDetails,
  setDeviceDetails,
} from 'src/store/actions/deviceActions';
import {
  getTicketDetails,
  setTicketDetails,
} from 'src/store/actions/ticketActions';
import { TicketStatus } from 'src/store/models/Ticket';
import { useLoadingSelector } from 'src/utils/useLoadingSelector';
import { useNonInitialEffect } from 'src/utils/useNonInitialEffect';

import classes from './ManufacturerTicketDetails.module.scss';

const ManufacturerTicketDetails: FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();

  const loading = useLoadingSelector({
    actionType: getTicketDetails.toString(),
  });
  const deviceLoading = useLoadingSelector({
    actionType: getDeviceDetails.toString(),
  });
  const ticket = useAppSelector((state) => state.ticket.ticketDetails);
  const isResolved = ticket?.status === TicketStatus.RESOLVED;
  const isInProgress = ticket?.status === TicketStatus.IN_PROGRESS;

  useEffect(() => {
    dispatch(setTicketDetails(null));
    dispatch(getTicketDetails({ id: params?.ticketId }));
    return () => {
      dispatch(setDeviceDetails(null));
    };
    // eslint-disable-next-line
  }, []);

  useNonInitialEffect(() => {
    if (ticket?.status === TicketStatus.UNASSIGNED) {
      navigate(routes.TICKETS, { replace: true });
    }
    // eslint-disable-next-line
  }, [ticket]);

  const headerRight = (
    <div className={classes.HeaderRight}>
      <StatusIndicator status={ticket?.status} />
      <div>
        <DateTooltip
          tooltipTitle={t('tickets:assign_date')}
          icon={faCalendarPlus}
          date={ticket?.assignedAt}
        />
        {(isInProgress || isResolved) && (
          <DateTooltip
            tooltipTitle={t('tickets:start_date')}
            icon={faCalendarEdit}
            date={ticket?.startedAt}
          />
        )}
        {isResolved && (
          <DateTooltip
            tooltipTitle={t('tickets:resolved_date')}
            icon={faCalendarCheck}
            date={ticket?.resolvedAt}
          />
        )}
        {ticket && !isResolved ? (
          <Button
            className={classes.HeaderButton}
            onClick={() => {
              navigate(`${routes.TICKETS}/${ticket?.id}${routes.REASSIGN}`);
            }}
          >
            {t('tickets:reassign')}
          </Button>
        ) : null}
      </div>
    </div>
  );

  return (
    <div className={classes.Container}>
      <PageHeader
        title={ticket ? `${t('common:ticket')} ${ticket?.ticketId}` : space}
        showBackButton
        endNode={ticket && headerRight}
      />
      <h4>{t('common:assignee')}</h4>
      <section>
        <Avatar
          className={classes.Avatar}
          title={ticket?.assignee?.title}
          image={ticket?.assignee?.image}
          role={ticket?.assignee?.company?.title}
          loading={loading}
          imageSize={32}
          onClick={() =>
            navigate(`${routes.EDIT_SERVICE_ACCOUNT}/${ticket?.assigneeId}`)
          }
        />
      </section>
      <h4>{t('ticket_details:ticket_details')}</h4>
      <section>
        <CustomerDetails ticket={ticket} loading={loading} />
        <aside>
          <DetectedErrors loading={loading || deviceLoading} />
          <TicketNotes
            mode="view"
            title={t('ticket_details:technicians_notes')}
            emptyText={t('ticket_details:no_technician_notes')}
          />
        </aside>
      </section>
      <HeatingApplianceDetails />
    </div>
  );
};

export default ManufacturerTicketDetails;
